import { Trans } from '@lingui/macro'

import * as React from 'react'

import { CartSummaryFixed } from '~ui/components/cart/CartSummaryFixed'

import { useCartContentCtx } from '../contexts/CartContentProvider'
import { getCartSum } from '../helpers/getCartSum'

type TBaseProps = PropsOf<typeof CartSummaryFixed>

type TCartSummaryFixedProps = Omit<
  TBaseProps,
  'onCartButtonClick' | 'cartSumLabel' | 'cartSum' | 'cartItemCount' | 'dataTestId'
>

type TProps = NoChildren &
  TCartSummaryFixedProps & {
    onCartButtonClick: TBaseProps['onCartButtonClick']
    /**
     * By default, there is a loading state rendered while cart content is being fetched. Use this prop to disable the loading state.
     */
    loadingDisabled?: boolean
  }

/**
 * Cart summary positioned to the bottom of the page that uses cart sum and item count from CartContentProvider.
 * It should be synchronized with the screen layout by `screenContentResponsiveWidth` prop
 * that sets the `maxWidth` and `width` of the component.
 */
export const CartSummaryTablet: React.FC<TProps> = ({
  onCartButtonClick,
  loadingDisabled = false,
  state,
  ...baseProps
}) => {
  const cartContent = useCartContentCtx()
  const cartItemCount = cartContent?.itemCount ?? 0
  const cartIsNotEmpty = cartItemCount > 0

  const isLoading = state === 'loading'
  const isCartSummaryLoading = (!cartContent || isLoading) && !loadingDisabled

  const cartSumLabel = <Trans>Cart total</Trans>
  const cartIsEmptyLabel = <Trans>cart is empty, for now...</Trans>

  return (
    <CartSummaryFixed
      {...baseProps}
      state={isCartSummaryLoading ? `loading` : `enabled`}
      bottom={`medium`}
      cartSumLabel={cartIsNotEmpty ? cartSumLabel : cartIsEmptyLabel}
      cartSum={cartIsNotEmpty ? getCartSum(cartContent) : undefined}
      cartItemCount={cartItemCount}
      onCartButtonClick={onCartButtonClick}
      showSkeleton={isLoading}
    />
  )
}
